import { i as listeners, n as emptyActor, r as isEmptyActor, t as actor } from "./context-ChQ0Q6hG.js";
import { c } from "react/compiler-runtime";
import { getDraftId, getPublishedId } from "@sanity/client/csm";
import { at, createIfNotExists, insert, patch, truncate } from "@sanity/mutate";
import { isMaybePreviewIframe, isMaybePreviewWindow } from "@sanity/presentation-comlink";
import { isIndexSegment, isIndexTuple, isKeySegment } from "@sanity/types";
import { useSyncExternalStore } from "react";
const rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g, reKeySegment = /_key\s*==\s*['"](.*)['"]/;
function get(obj, path, defaultVal) {
	let select = typeof path == "string" ? fromString(path) : path;
	if (!Array.isArray(select)) throw Error("Path must be an array or a string");
	let acc = obj;
	for (let i = 0; i < select.length; i++) {
		let segment = select[i];
		if (isIndexSegment(segment)) {
			if (!Array.isArray(acc)) return defaultVal;
			acc = acc[segment];
		}
		if (isKeySegment(segment)) {
			if (!Array.isArray(acc)) return defaultVal;
			acc = acc.find((item) => item._key === segment._key);
		}
		if (typeof segment == "string" && (acc = typeof acc == "object" && acc ? acc[segment] : void 0), acc === void 0) return defaultVal;
	}
	return acc;
}
function fromString(path) {
	if (typeof path != "string") throw Error("Path is not a string");
	let segments = path.match(rePropName);
	if (!segments) throw Error("Invalid path string");
	return segments.map(normalizePathSegment);
}
function normalizePathSegment(segment) {
	return isIndexSegment(segment) ? normalizeIndexSegment(segment) : isKeySegment(segment) ? normalizeKeySegment(segment) : isIndexTuple(segment) ? normalizeIndexTupleSegment(segment) : segment;
}
function normalizeIndexSegment(segment) {
	return Number(segment.replace(/[^\d]/g, ""));
}
function normalizeKeySegment(segment) {
	return { _key: segment.match(reKeySegment)[1] };
}
function normalizeIndexTupleSegment(segment) {
	let [from, to] = segment.split(":").map((seg) => seg === "" ? seg : Number(seg));
	return [from, to];
}
function useOptimisticActor() {
	return useSyncExternalStore(_temp, _temp2, _temp3);
}
function _temp3() {
	return emptyActor;
}
function _temp2() {
	return actor;
}
function _temp(listener) {
	return listeners.add(listener), () => listeners.delete(listener);
}
function useOptimisticActorReady() {
	let $ = c(2), actor = useOptimisticActor(), t0;
	return $[0] === actor ? t0 = $[1] : (t0 = isEmptyActor(actor), $[0] = actor, $[1] = t0), !t0;
}
function debounce(fn, timeout) {
	let timer;
	return ((...args) => {
		clearTimeout(timer), timer = setTimeout(() => {
			fn.apply(fn, args);
		}, timeout);
	});
}
function getDocumentsAndSnapshot(id, actor) {
	let inFrame = isMaybePreviewIframe(), inPopUp = isMaybePreviewWindow();
	if (isEmptyActor(actor) || !inFrame && !inPopUp) throw Error("The `useDocuments` hook cannot be used in this context");
	let draftId = getDraftId(id), publishedId = getPublishedId(id), documents = actor.getSnapshot().context?.documents, draftDoc = documents?.[draftId], publishedDoc = documents?.[publishedId], doc = draftDoc || publishedDoc;
	if (!doc) throw Error(`Document "${id}" not found`);
	let getDocumentSnapshot = () => draftDoc.getSnapshot().context?.local || publishedDoc.getSnapshot().context?.local, snapshot = getDocumentSnapshot(), snapshotPromise = new Promise((resolve) => {
		if (snapshot) resolve(snapshot);
		else {
			let subscriber = doc.on("ready", (event) => {
				let { snapshot } = event;
				resolve(snapshot || null), subscriber.unsubscribe();
			});
		}
	}), getSnapshot = () => snapshotPromise;
	return {
		draftDoc,
		draftId,
		getSnapshot,
		publishedDoc,
		publishedId,
		/**
		* @deprecated - use `getSnapshot` instead
		*/
		get snapshot() {
			if (!snapshot) throw Error(`Snapshot for document "${id}" not found`);
			return snapshot;
		}
	};
}
function createDocumentCommit(id, actor) {
	return () => {
		let { draftDoc } = getDocumentsAndSnapshot(id, actor);
		draftDoc.send({ type: "submit" });
	};
}
/**
* @deprecated - superseded by `createDocumentGetSnapshot`
*/
function createDocumentGet(id, actor) {
	return (path) => {
		let { snapshot } = getDocumentsAndSnapshot(id, actor);
		return path ? get(snapshot, path) : snapshot;
	};
}
function createDocumentGetSnapshot(id, actor) {
	let { getSnapshot } = getDocumentsAndSnapshot(id, actor);
	return getSnapshot;
}
function createDocumentPatch(id, actor) {
	return async (patches, options) => {
		let result = getDocumentsAndSnapshot(id, actor), { draftDoc, draftId, getSnapshot, publishedId } = result, { commit = !0 } = options || {}, resolvedPatches = await (typeof patches == "function" ? patches({
			draftId,
			publishedId,
			/**
			* @deprecated - use `getSnapshot` instead
			*/
			get snapshot() {
				return result.snapshot;
			},
			getSnapshot
		}) : patches), _snapshot = await getSnapshot();
		if (!_snapshot) throw Error(`Snapshot for document "${id}" not found`);
		draftDoc.send({
			type: "mutate",
			mutations: [createIfNotExists({
				..._snapshot,
				_id: draftId
			}), patch(draftId, resolvedPatches)]
		}), commit && (typeof commit == "object" && "debounce" in commit ? debounce(() => draftDoc.send({ type: "submit" }), commit.debounce)() : draftDoc.send({ type: "submit" }));
	};
}
function useDocuments() {
	let $ = c(7), actor = useOptimisticActor(), t0;
	$[0] === actor ? t0 = $[1] : (t0 = (documentId) => ({
		id: documentId,
		commit: createDocumentCommit(documentId, actor),
		get: createDocumentGet(documentId, actor),
		getSnapshot: createDocumentGetSnapshot(documentId, actor),
		patch: createDocumentPatch(documentId, actor)
	}), $[0] = actor, $[1] = t0);
	let getDocument = t0, t1;
	$[2] === actor ? t1 = $[3] : (t1 = (id, mutations, options) => {
		let { draftDoc } = getDocumentsAndSnapshot(id, actor), { commit: t2 } = options || {}, commit = t2 === void 0 || t2;
		draftDoc.send({
			type: "mutate",
			mutations
		}), commit && (typeof commit == "object" && "debounce" in commit ? debounce(() => draftDoc.send({ type: "submit" }), commit.debounce)() : draftDoc.send({ type: "submit" }));
	}, $[2] = actor, $[3] = t1);
	let mutateDocument = t1, t2;
	return $[4] !== getDocument || $[5] !== mutateDocument ? (t2 = {
		getDocument,
		mutateDocument
	}, $[4] = getDocument, $[5] = mutateDocument, $[6] = t2) : t2 = $[6], t2;
}
function whatwgRNG(length = 16) {
	let rnds8 = new Uint8Array(length);
	return crypto.getRandomValues(rnds8), rnds8;
}
const getByteHexTable = (() => {
	let table;
	return () => {
		if (table) return table;
		table = [];
		for (let i = 0; i < 256; ++i) table[i] = (i + 256).toString(16).slice(1);
		return table;
	};
})();
function randomKey(length) {
	let table = getByteHexTable();
	return whatwgRNG(length).reduce((str, n) => str + table[n], "").slice(0, length);
}
function getArrayItemKeyAndParentPath(pathOrNode) {
	let elementPath = typeof pathOrNode == "string" ? pathOrNode : pathOrNode.path, lastDotIndex = elementPath.lastIndexOf("."), lastPathItem = elementPath.substring(lastDotIndex + 1, elementPath.length);
	if (!lastPathItem.indexOf("[")) throw Error("Invalid path: not an array");
	let lastArrayIndex = elementPath.lastIndexOf("["), path = elementPath.substring(0, lastArrayIndex), key, hasExplicitKey;
	if (lastPathItem.includes("_key")) {
		let startIndex = lastPathItem.indexOf("\"") + 1, endIndex = lastPathItem.indexOf("\"", startIndex);
		key = lastPathItem.substring(startIndex, endIndex), hasExplicitKey = !0;
	} else {
		let startIndex = lastPathItem.indexOf("[") + 1, endIndex = lastPathItem.indexOf("]", startIndex);
		key = lastPathItem.substring(startIndex, endIndex), hasExplicitKey = !1;
	}
	if (!path || !key) throw Error("Invalid path");
	return {
		path,
		key,
		hasExplicitKey
	};
}
function getArrayDuplicatePatches(node, snapshot, position = "after") {
	let { path: arrayPath, key: itemKey } = getArrayItemKeyAndParentPath(node), duplicate = {
		...get(snapshot, node.path),
		_key: randomKey()
	};
	return [at(arrayPath, insert(duplicate, position, { _key: itemKey }))];
}
function getArrayRemovePatches(node, snapshot) {
	let { path: arrayPath, key: itemKey } = getArrayItemKeyAndParentPath(node), currentIndex = get(snapshot, arrayPath).findIndex((item) => item._key === itemKey);
	return [at(arrayPath, truncate(currentIndex, currentIndex + 1))];
}
function getArrayInsertPatches(node, insertType, position) {
	let { path: arrayPath, key: itemKey } = getArrayItemKeyAndParentPath(node), insertKey = randomKey();
	return [at(arrayPath, insert([{
		_type: insertType,
		_key: insertKey
	}], position, { _key: itemKey }))];
}
async function getArrayMovePatches(node, doc, moveTo) {
	if (!node.type) throw Error("Node type is missing");
	let { path: arrayPath, key: itemKey } = getArrayItemKeyAndParentPath(node), snapshot = await doc.getSnapshot(), array = get(snapshot, arrayPath), item = get(snapshot, node.path), currentIndex = array.findIndex((item) => item._key === itemKey), nextIndex = -1, position = "before";
	if (moveTo === "first") {
		if (currentIndex === 0) return [];
		nextIndex = 0, position = "before";
	} else if (moveTo === "last") {
		if (currentIndex === array.length - 1) return [];
		nextIndex = -1, position = "after";
	} else if (moveTo === "next") {
		if (currentIndex === array.length - 1) return [];
		nextIndex = currentIndex, position = "after";
	} else if (moveTo === "previous") {
		if (currentIndex === 0) return [];
		nextIndex = currentIndex - 1, position = "before";
	}
	return [at(arrayPath, truncate(currentIndex, currentIndex + 1)), at(arrayPath, insert(item, position, nextIndex))];
}
export { getArrayRemovePatches as a, useOptimisticActorReady as c, getArrayMovePatches as i, get as l, getArrayInsertPatches as n, useDocuments as o, getArrayItemKeyAndParentPath as r, useOptimisticActor as s, getArrayDuplicatePatches as t };

//# sourceMappingURL=mutations-D78SQhfO.js.map